/**
 * Where `MindspaceGraph` should put its camera so every concept is on screen —
 * ROUTE_INVENTORY §2.10 #25.
 *
 * ## Why not the library's `zoomToFit`
 *
 * `react-force-graph`'s `zoomToFit(ms, padding)` frames node CENTRES. Two
 * things it cannot know broke every Knowledge Archive frame:
 *
 * - **Labels.** Above `LABEL_VISIBLE_ZOOM` each node paints a pill under
 *   itself, a constant size in SCREEN pixels. Framing centres left the outer
 *   labels half off the canvas ("ross Entropy").
 * - **Small graphs.** Four concepts a few hundred units apart fit at 250%+, so
 *   a search result blew up into a couple of giant dots with the rest of the
 *   canvas empty. The fit is capped at `FIT_MAX_ZOOM` instead.
 *
 * It was also called on a 400ms timer, while the simulation was still
 * spreading the nodes — so it framed a layout that no longer existed a second
 * later. When to fit is the component's business; this module only answers
 * "given where the nodes are now, what camera shows all of them".
 *
 * ## The maths
 *
 * A node at graph `x` with radius `r` and a label `w` pixels wide spans screen
 * `[k(x - r) - w/2, k(x + r) + w/2]` relative to the camera, for scale `k`.
 * The whole graph's span is therefore piecewise-linear and increasing in `k`,
 * so the largest `k` that fits is found by bisection — exact enough, and no
 * special-casing of one node, two nodes or a label that dominates the width.
 * Labels only exist at `k >= LABEL_VISIBLE_ZOOM`, which makes "fits" drop from
 * true to false there at worst: still monotone, still bisectable.
 */

/** Labels paint only above this scale. Shared with the renderer. */
export const LABEL_VISIBLE_ZOOM = 1.4;

/**
 * The phone graph's label scale. At 390px the scale that fits a whole graph
 * sits well under 1.4, so the desktop threshold left every phone frame a
 * field of unnamed dots; this one names a small graph as it lands, and a
 * big one once you pinch in a little.
 */
export const PHONE_LABEL_ZOOM = 0.8;

/** The camera never fits tighter than this, however few nodes there are. */
export const FIT_MAX_ZOOM = 2;

/** …nor looser than the graph's own `minZoom`. */
export const FIT_MIN_ZOOM = 0.2;

/** Clear space kept between the outermost node or label and the canvas edge. */
export const FIT_PADDING_PX = 40;

/**
 * How far below a node's disc its label reaches, in screen pixels: the 3px gap
 * plus the pill (11px text + 2 × 2px padding). Mirrors `drawNode`.
 */
export const LABEL_DROP_PX = 3 + 11 + 4;

/**
 * Longest label the renderer paints before truncating, so a long concept name
 * cannot overlap two or three neighbouring nodes. Shared with `drawNode`.
 */
export const MAX_LABEL_CHARS = 22;

/** The label text `drawNode` paints for a concept name. */
export const truncateLabel = (name: string) =>
  name.length > MAX_LABEL_CHARS
    ? name.slice(0, MAX_LABEL_CHARS - 1) + '…'
    : name;

/** The canvas font labels paint in, at 1× — `drawNode` scales the size. */
export const LABEL_FONT_PX = 11;
export const labelFont = (px: number) =>
  `500 ${px}px "Inter Variable", Inter, system-ui, sans-serif`;

let measureContext: CanvasRenderingContext2D | null | undefined;

/**
 * A label pill's width in screen pixels: the truncated text at 11px plus 4px
 * padding either side, exactly as `drawNode` sizes it. Falls back to an
 * average glyph width where there is no canvas (jsdom).
 */
export function labelWidthPx(name: string): number {
  const text = truncateLabel(name);
  if (measureContext === undefined) {
    measureContext =
      typeof document === 'undefined'
        ? null
        : document.createElement('canvas').getContext('2d');
  }
  if (!measureContext) return text.length * 6.5 + 8;
  measureContext.font = labelFont(LABEL_FONT_PX);
  return measureContext.measureText(text).width + 8;
}

export interface FitNode {
  x?: number;
  y?: number;
  /** Radius in graph units. */
  size?: number;
  name: string;
}

export interface FitViewport {
  /** Scale to apply. */
  k: number;
  /** Graph coordinate to centre the canvas on. */
  x: number;
  y: number;
}

interface Extent {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

type Placed = { x: number; y: number; r: number; w: number };

function extentAt(nodes: Placed[], k: number, labels: boolean): Extent {
  const e = {
    left: Infinity,
    right: -Infinity,
    top: Infinity,
    bottom: -Infinity,
  };
  for (const n of nodes) {
    const half = labels ? n.w / 2 : 0;
    e.left = Math.min(e.left, k * (n.x - n.r) - half);
    e.right = Math.max(e.right, k * (n.x + n.r) + half);
    e.top = Math.min(e.top, k * (n.y - n.r));
    e.bottom = Math.max(
      e.bottom,
      k * (n.y + n.r) + (labels ? LABEL_DROP_PX : 0)
    );
  }
  return e;
}

/**
 * The camera that frames every positioned node, labels included, inside
 * `width × height` less `FIT_PADDING_PX` on each side. `null` when there is
 * nothing to frame yet (no nodes, or none positioned, or an unmeasured box).
 *
 * `labelWidthPx(name)` is the rendered label pill's width in screen pixels —
 * injected because measuring text needs a canvas the tests do not have.
 */
export function computeFitViewport(
  nodes: FitNode[],
  width: number,
  height: number,
  labelWidthPx: (name: string) => number,
  labelZoom: number = LABEL_VISIBLE_ZOOM
): FitViewport | null {
  const placed: Placed[] = nodes
    .filter(n => Number.isFinite(n.x) && Number.isFinite(n.y))
    .map(n => ({
      x: n.x as number,
      y: n.y as number,
      r: n.size ?? 4,
      w: labelWidthPx(n.name),
    }));
  const availW = width - 2 * FIT_PADDING_PX;
  const availH = height - 2 * FIT_PADDING_PX;
  if (!placed.length || availW <= 0 || availH <= 0) return null;

  const fits = (k: number) => {
    const e = extentAt(placed, k, k >= labelZoom);
    return e.right - e.left <= availW && e.bottom - e.top <= availH;
  };

  let k = FIT_MAX_ZOOM;
  if (!fits(k)) {
    let lo = FIT_MIN_ZOOM;
    let hi = FIT_MAX_ZOOM;
    for (let i = 0; i < 40; i += 1) {
      const mid = (lo + hi) / 2;
      if (fits(mid)) lo = mid;
      else hi = mid;
    }
    k = lo;
  }

  const e = extentAt(placed, k, k >= labelZoom);
  return { k, x: (e.left + e.right) / 2 / k, y: (e.top + e.bottom) / 2 / k };
}

/**
 * How many nodes sit fully inside the canvas under the camera actually applied
 * — the disc, and its label when labels are showing. `toScreen` is the
 * library's own `graph2ScreenCoords`, so this reads the real transform rather
 * than the one `computeFitViewport` asked for. The component publishes the
 * result as `data-nodes-in-view`, because a canvas has no DOM for a test to
 * query.
 */
interface InViewOptions {
  /** The scale actually applied. */
  k: number;
  /** The library's own `graph2ScreenCoords`. */
  toScreen: (x: number, y: number) => { x: number; y: number };
  width: number;
  height: number;
  labelWidthPx: (name: string) => number;
  /** The scale labels show from; `LABEL_VISIBLE_ZOOM` when left out. */
  labelZoom?: number;
}

/** Whether one node's disc — and label, when showing — is fully on canvas. */
function isNodeInView(
  n: FitNode,
  {
    k,
    toScreen,
    width,
    height,
    labelWidthPx: labelW,
    labelZoom = LABEL_VISIBLE_ZOOM,
  }: InViewOptions
): boolean {
  if (!Number.isFinite(n.x) || !Number.isFinite(n.y)) return false;
  const labels = k >= labelZoom;
  const r = (n.size ?? 4) * k;
  const { x, y } = toScreen(n.x as number, n.y as number);
  const half = Math.max(r, labels ? labelW(n.name) / 2 : 0);
  const below = r + (labels ? LABEL_DROP_PX : 0);
  return (
    x - half >= 0 && x + half <= width && y - r >= 0 && y + below <= height
  );
}

export function countNodesInView(
  nodes: FitNode[],
  options: InViewOptions
): number {
  return nodes.filter(n => isNodeInView(n, options)).length;
}
